/**
 * Tab time controls — editor-facing alignment check (PRD-45 Module 2). Split
 * out of tab-time-controls.ts to keep that engine file focused; re-exported from
 * there so consumers keep importing from "@utils/tab-time-controls".
 */
import type {
  DateGuardrails,
  GuardrailRollingOption,
  TimeSeriesViewerOptions,
  PointInTimeViewerOptions,
} from "@models/Dataset";
import type { WidgetResponse } from "@models/Widget";
import {
  buildFamilyMembers,
  getSharedTimeConfig,
  viewerContractKey,
  type SharedTimeConfig,
  type TabTimeFamilyKind,
} from "./tab-time-controls";
import {
  filterTimeSeriesViewerOptions,
  filterPointInTimeViewerOptions,
} from "./viewer-time-guardrails";

/**
 * For the visualization being configured, look at the OTHER visualizations in
 * the same family + dataset on the tab and report whether they already agree on
 * one tab-time configuration.
 *   - "aligned" → they share one config (reuse it as the suggestion)
 *   - "varies"  → they exist but disagree
 *   - "none"    → there are no other comparable visualizations
 * Reuses `getSharedTimeConfig`, so the agreement rule matches the popover.
 */
export type TabTimeAlignment =
  | { status: "aligned"; count: number; config: SharedTimeConfig }
  | { status: "varies"; count: number }
  | { status: "none" };

export function getTabTimeAlignment(params: {
  widgets: WidgetResponse[] | undefined;
  kind: TabTimeFamilyKind;
  datasetId: string | null;
  /** The widget being edited — excluded from the comparison. null for a new one. */
  currentWidgetId: string | null;
  guardrailsByDataset?: Record<string, DateGuardrails>;
}): TabTimeAlignment {
  const { widgets, kind, datasetId, currentWidgetId, guardrailsByDataset } =
    params;
  if (!datasetId) return { status: "none" };

  const members = buildFamilyMembers(widgets, guardrailsByDataset)[kind].filter(
    (m) => m.datasetId === datasetId && m.widgetId !== currentWidgetId,
  );
  if (members.length === 0) return { status: "none" };

  const config = getSharedTimeConfig(members, kind);
  return config
    ? { status: "aligned", count: members.length, config }
    : { status: "varies", count: members.length };
}

/** Guardrail-filter an in-progress edited contract, matching how saved members
 *  are filtered, so contract comparison is apples-to-apples. */
function filterEditedViewerOptions(
  viewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions,
  kind: TabTimeFamilyKind,
  savedSetting: string,
  guardrails: DateGuardrails | undefined,
): TimeSeriesViewerOptions | PointInTimeViewerOptions {
  if (!guardrails) return viewerOptions;
  return kind === "trend"
    ? filterTimeSeriesViewerOptions(
        viewerOptions as TimeSeriesViewerOptions,
        savedSetting,
        guardrails,
      )
    : filterPointInTimeViewerOptions(
        viewerOptions as PointInTimeViewerOptions,
        savedSetting,
        guardrails,
      );
}

/**
 * Pre-save break check (Module 4). When the visualization being edited was part
 * of a valid shared configuration (its saved config plus its siblings all agree)
 * AND the in-progress edit would change its saved default or viewer-time
 * contract, returns the shared config that is being broken (so the editor can
 * offer to restore it). Returns null when there is no alignment to break.
 */
export function getTabTimeBreak(params: {
  widgets: WidgetResponse[] | undefined;
  kind: TabTimeFamilyKind;
  datasetId: string | null;
  currentWidgetId: string | null;
  editedSetting: string | null;
  editedRA: GuardrailRollingOption;
  editedViewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions;
  guardrailsByDataset?: Record<string, DateGuardrails>;
}): { shared: SharedTimeConfig } | null {
  const {
    widgets,
    kind,
    datasetId,
    currentWidgetId,
    editedSetting,
    editedRA,
    editedViewerOptions,
    guardrailsByDataset,
  } = params;
  if (!datasetId || !currentWidgetId) return null;

  const members = buildFamilyMembers(widgets, guardrailsByDataset)[kind].filter(
    (m) => m.datasetId === datasetId,
  );
  // Needs the edited widget plus ≥1 sibling — a lone chart has no shared config
  // (and the tab control isn't even shown), so there is nothing to break.
  if (members.length < 2) return null;
  if (!members.some((m) => m.widgetId === currentWidgetId)) return null;

  // The whole family (as saved, incl. this widget) must currently be aligned.
  const shared = getSharedTimeConfig(members, kind);
  if (!shared) return null;

  const filteredEdited = editedSetting
    ? filterEditedViewerOptions(
        editedViewerOptions,
        kind,
        editedSetting,
        guardrailsByDataset?.[datasetId],
      )
    : editedViewerOptions;

  const diverges =
    editedSetting !== shared.savedDefault ||
    editedRA !== shared.savedRollingAverage ||
    editedViewerOptions.enabled !== true ||
    viewerContractKey(filteredEdited, kind) !==
      viewerContractKey(shared.viewerOptions, kind);

  return diverges ? { shared } : null;
}
